import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { ReportType } from '@/app/constants/reportType'
import type { PreviewItem } from './printPreviewStore'

/**
 * Wydruk grupowy nie otwiera osobnego podgladu dla kazdego dokumentu - parytet
 * z WPF, gdzie petla drukujaca raportuje tylko po ostatnim raporcie. Zbieramy
 * gotowe dokumenty i po zakonczeniu calej paczki otwieramy jeden podglad z
 * przewijaniem miedzy nimi.
 *
 * Przy wiecej niz jednym kierowcy raporty generuja sie do osobnych plikow, wiec
 * jedno zadanie zwraca komplet dokumentow - po jednym na kierowce.
 */
export type BatchDocStatus = 'pending' | 'ready' | 'error'

export type BatchPrintoutDoc = {
  taskId: string
  reportType: ReportType | null
  status: BatchDocStatus
  files: PreviewItem[]
  isPdf?: boolean
  errorDetails?: string
}

type State = {
  docs: BatchPrintoutDoc[]
  /** Nazwy kierowcow/pojazdow objetych paczka - z nich powstaja foldery w ZIP. */
  subjects: string[]
  /** Paczka zamknieta - znamy juz komplet zadan i czekamy tylko na wyniki. */
  sealed: boolean
}

type Actions = {
  startBatch: (subjects: string[]) => void
  trackTask: (taskId: string, reportType: ReportType | null) => void
  sealBatch: () => void
  isTracked: (taskId: string) => boolean
  getSubjects: () => string[]
  resolveTask: (
    taskId: string,
    patch: Partial<Omit<BatchPrintoutDoc, 'taskId'>> & { status: BatchDocStatus },
  ) => void
  /**
   * Wyniki paczki po jej zakonczeniu (i wyczyszczenie stanu); null dopoki
   * ktorykolwiek dokument jeszcze sie generuje.
   */
  takeFinishedBatch: () => BatchPrintoutDoc[] | null
}

const isFinished = (docs: BatchPrintoutDoc[], sealed: boolean) =>
  sealed && docs.length > 0 && docs.every((doc) => doc.status !== 'pending')

export const useBatchPrintoutStore = create<State & Actions>()(
  devtools(
    (set, get) => ({
      docs: [],
      subjects: [],
      sealed: false,

      startBatch: (subjects) =>
        set(
          (s) => {
            // Porzucona paczka (np. zadanie, ktore nigdy sie nie zakonczylo)
            // nie moze zostawic po sobie adresow blob.
            s.docs.forEach((doc) =>
              doc.files.forEach((file) => URL.revokeObjectURL(file.blobUrl)),
            )
            return { docs: [], subjects, sealed: false }
          },
          false,
          'startBatch',
        ),

      trackTask: (taskId, reportType) =>
        set(
          (s) => {
            if (s.docs.some((doc) => doc.taskId === taskId)) return {}
            return {
              docs: [
                ...s.docs,
                { taskId, reportType, status: 'pending' as const, files: [] },
              ],
            }
          },
          false,
          'trackTask',
        ),

      sealBatch: () => set({ sealed: true }, false, 'sealBatch'),

      isTracked: (taskId) => get().docs.some((doc) => doc.taskId === taskId),

      getSubjects: () => get().subjects,

      resolveTask: (taskId, patch) =>
        set(
          (s) => {
            if (!s.docs.some((doc) => doc.taskId === taskId)) return {}
            return {
              docs: s.docs.map((doc) =>
                doc.taskId === taskId ? { ...doc, ...patch } : doc,
              ),
            }
          },
          false,
          'resolveTask',
        ),

      takeFinishedBatch: () => {
        const { docs, sealed } = get()
        if (!isFinished(docs, sealed)) return null
        set({ docs: [], subjects: [], sealed: false }, false, 'batchFinished')
        return docs
      },
    }),
    { name: 'EasyTacho', store: 'batch-printout' },
  ),
)
